import React from 'react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface CategoryIndicatorProps {
  label: string;
  contribution: number;
  totalWeight: number;
}

const CATEGORY_DESCRIPTIONS: Record<string, string> = {
  "Outcomes": "Measures graduation rates, retention rates, and post-graduation success (52% of total score)",
  "Expert Opinion": "Based on peer assessment scores from other institutions (20% of total score)",
  "Faculty Resources": "Evaluates faculty qualifications, class sizes, and salaries (11% of total score)",
  "Financial Resources": "Assesses spending per student on academic programs (8% of total score)",
  "Student Excellence": "Considers standardized test scores and academic achievements (5% of total score)",
  "Faculty Research": "Measures research output and scholarly impact (4% of total score)"
};

export const CategoryIndicator: React.FC<CategoryIndicatorProps> = ({
  label,
  contribution,
  totalWeight
}) => {
  const radius = 28;
  const circumference = 2 * Math.PI * radius;
  const strokeWidth = 6;
  
  // Calculate fill percentage based on contribution relative to total possible (100%)
  const fillPercentage = (contribution / 100) * 100;
  const strokeDashoffset = circumference - (fillPercentage / 100) * circumference;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="flex flex-col items-center group w-full">
            <div className="relative w-16 h-16 mb-2">
              <svg className="w-16 h-16 transform -rotate-90">
                {/* Background circle */}
                <circle
                  className="stroke-gray-100"
                  strokeWidth={strokeWidth}
                  fill="transparent"
                  r={radius}
                  cx="32"
                  cy="32"
                />
                {/* Progress circle */}
                <circle
                  className="stroke-[#0057B7] transition-all duration-500"
                  strokeWidth={strokeWidth}
                  strokeLinecap="round"
                  fill="transparent"
                  r={radius}
                  cx="32"
                  cy="32"
                  style={{
                    strokeDasharray: circumference,
                    strokeDashoffset: strokeDashoffset
                  }}
                />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <div className="text-lg font-bold text-gray-900">{contribution}%</div>
              </div>
            </div>
            <div className="text-sm font-medium text-center text-gray-900 whitespace-nowrap">
              {label}
            </div>
          </div>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="max-w-[200px] text-center p-3">
          <p className="font-medium mb-1">{label}</p>
          <p className="text-sm text-gray-500">{CATEGORY_DESCRIPTIONS[label]}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};